/**
 * MenuSelect — Selector tipo lista anclado al control (sin <select> nativo).
 *
 * QUÉ HACE: Misma línea visual que `Select` de Input.tsx, pero abre un panel
 *   con opciones en la página. Evita el picker a pantalla completa del SO en móvil.
 *
 * QUÉ EXPORTA: MenuSelect, MenuSelectOption
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";

export interface MenuSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Texto secundario en la fila (opcional) */
  hint?: string;
  /** Acción opcional de edición directamente en la fila */
  onEdit?: (e: React.MouseEvent) => void;
  /** Acción opcional de eliminación directamente en la fila */
  onDelete?: (e: React.MouseEvent) => void;
  /** Si true, el icono de borrar sale desactivado (ej: caja abierta) */
  deleteDisabled?: boolean;
}

interface MenuSelectProps {
  value: string;
  options: MenuSelectOption[];
  onChange: (value: string) => void;
  /** Si se pasa, reemplaza el label inferido de `options` en el botón */
  buttonTextOverride?: string;
  disabled?: boolean;
  className?: string;
  /** Para asociar con <label htmlFor="..."> */
  id?: string;
  title?: string;
  /** Altura máx. del panel (p. ej. 5 filas visibles + scroll). Por defecto ~15 filas. */
  listMaxHeightClass?: string;
  /** Trigger más compacto (apertura de caja / móvil). */
  compact?: boolean;
}

const triggerBase =
  "w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm text-left border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:text-gray-500 disabled:cursor-not-allowed bg-white dark:bg-slate-800 dark:text-gray-100";
const triggerRadiusDefault = "rounded-xl";
const triggerRadiusCompact = "rounded-lg";
const listRadiusCompact = "rounded-lg";
const triggerState =
  "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500";

export const MenuSelect = ({
  value,
  options,
  onChange,
  buttonTextOverride,
  disabled = false,
  className = "",
  id,
  title,
  listMaxHeightClass = "max-h-60",
  compact = false,
}: MenuSelectProps) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value);
  const buttonLabel =
    buttonTextOverride ?? selected?.label ?? options[0]?.label ?? "Seleccionar";

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!disabled) setOpen((o) => !o);
    }
    if (e.key === "ArrowDown" && !open && !disabled) {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        title={title}
        className={`${triggerBase} ${triggerState} ${
          compact ? triggerRadiusCompact : triggerRadiusDefault
        } ${
          compact
            ? "px-2 py-2 text-[11px] font-semibold shadow-inner border-gray-200 dark:border-slate-600 focus:ring-primary-500/30 focus:border-primary-400"
            : ""
        }`}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          className={`absolute z-[80] mt-1 w-full ${listMaxHeightClass} overflow-y-auto overscroll-contain border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 py-1 shadow-lg scrollbar-thin overflow-x-hidden ${
            compact ? listRadiusCompact : "rounded-xl"
          }`}
        >
          {options.map((opt) => {
            const isSel = opt.value === value;
            return (
              <li
                key={opt.value}
                role="presentation"
                className={`flex items-center gap-1 transition-colors ${
                  isSel
                    ? "bg-primary-50 dark:bg-primary-900/30"
                    : "hover:bg-gray-50 dark:hover:bg-slate-700/80"
                }`}
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  disabled={opt.disabled}
                  className={`flex-1 text-left px-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    compact ? "py-2 text-xs" : "py-2 text-sm"
                  } ${
                    isSel
                      ? "text-primary-800 dark:text-primary-200 font-semibold"
                      : "text-gray-800 dark:text-gray-100"
                  }`}
                  onClick={() => {
                    if (opt.disabled) return;
                    onChange(opt.value);
                    close();
                  }}
                >
                  <span className="block truncate">{opt.label}</span>
                  {opt.hint ? (
                    <span className="block text-[10px] text-gray-400 font-normal truncate">
                      {opt.hint}
                    </span>
                  ) : null}
                </button>

                <div className="flex items-center gap-1 shrink-0 pr-2">
                  {opt.onEdit && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        opt.onEdit?.(e);
                      }}
                      className="p-1 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-md text-primary-600 dark:text-primary-400 transition-colors"
                      title="Editar alias"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                  {opt.onDelete && (
                    <button
                      type="button"
                      disabled={opt.deleteDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        opt.onDelete?.(e);
                      }}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-md text-red-500 dark:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={
                        opt.deleteDisabled
                          ? "No puedes eliminar una caja con sesión abierta"
                          : "Eliminar registro"
                      }
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
